/**
 * Currency Service for Multi-Currency Support
 * Handles currency formatting, conversion, and localization
 */

// Currency configuration with symbols and formatting rules
export const CURRENCY_CONFIG = {
  ZMW: {
    code: "K",
    symbol: "K",
    name: "Zambian Kwacha",
    locale: "en-ZM",
    decimalPlaces: 2,
  },
  USD: {
    code: "USD",
    symbol: "$",
    name: "US Dollar",
    locale: "en-US",
    decimalPlaces: 2,
  },
  ZAR: {
    code: "ZAR",
    symbol: "R",
    name: "South African Rand",
    locale: "en-ZA",
    decimalPlaces: 2,
  },
  KES: {
    code: "KES",
    symbol: "KSh",
    name: "Kenyan Shilling",
    locale: "en-KE",
    decimalPlaces: 2,
  },
  BWP: {
    code: "BWP",
    symbol: "P",
    name: "Botswana Pula",
    locale: "en-BW",
    decimalPlaces: 2,
  },
  NAD: {
    code: "NAD",
    symbol: "N$",
    name: "Namibian Dollar",
    locale: "en-NA",
    decimalPlaces: 2,
  },
};

/**
 * Get current company's currency from context
 * Falls back to ZMW if not set
 */
export const getCurrentCurrency = (company) => {
  return company?.currency_code || "ZMW";
};

/**
 * Get currency configuration
 */
export const getCurrencyConfig = (currencyCode) => {
  return CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.ZMW;
};

/**
 * Format amount with currency
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - Currency code (ZMW, USD, etc.)
 * @param {object} options - Formatting options
 */
export const formatCurrency = (amount, currencyCode = "ZMW", options = {}) => {
  const config = getCurrencyConfig(currencyCode);
  const numAmount = parseFloat(amount) || 0;

  const { showSymbol = true, showCode = false, compact = false } = options;

  // Format number with locale
  let formatted;

  if (compact && numAmount >= 1000000) {
    formatted = `${(numAmount / 1000000).toFixed(1)}M`;
  } else if (compact && numAmount >= 1000) {
    formatted = `${(numAmount / 1000).toFixed(1)}K`;
  } else {
    formatted = numAmount.toLocaleString(config.locale, {
      minimumFractionDigits: config.decimalPlaces,
      maximumFractionDigits: config.decimalPlaces,
    });
  }

  // Build final string
  let result = "";
  if (showSymbol) {
    result = `${config.symbol} ${formatted}`;
  } else if (showCode) {
    result = `${formatted} ${config.code}`;
  } else {
    result = formatted;
  }

  return result;
};

/**
 * Format for Ant Design InputNumber formatter
 */
export const getAntdFormatter = (currencyCode = "ZMW") => {
  const config = getCurrencyConfig(currencyCode);
  return (value) =>
    `${config.code} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Parser for Ant Design InputNumber
 */
export const getAntdParser = (currencyCode = "ZMW") => {
  const config = getCurrencyConfig(currencyCode);
  return (value) =>
    value.replace(new RegExp(`${config.code}\\s?|(,*)`, "g"), "");
};

/**
 * Get currency symbol only
 */
export const getCurrencySymbol = (currencyCode = "ZMW") => {
  const config = getCurrencyConfig(currencyCode);
  return config.symbol;
};

/**
 * Get currency prefix/suffix for Ant Design components
 */
export const getCurrencyAffix = (currencyCode = "ZMW", type = "prefix") => {
  const config = getCurrencyConfig(currencyCode);

  if (type === "prefix") {
    return config.code;
  } else {
    return config.symbol;
  }
};

/**
 * Format currency for table displays
 */
export const formatCurrencyForTable = (amount, currencyCode = "ZMW") => {
  return formatCurrency(amount, currencyCode, { showSymbol: true });
};

/**
 * Get all available currencies for dropdowns
 */
export const getCurrencyOptions = () => {
  return Object.entries(CURRENCY_CONFIG).map(([code, config]) => ({
    label: `${config.name} (${config.code})`,
    value: code,
    symbol: config.symbol,
  }));
};

/**
 * Convert amount between currencies (placeholder for future exchange rate integration)
 */
export const convertCurrency = (
  amount,
  fromCurrency,
  toCurrency,
  exchangeRates = {}
) => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  // This would integrate with a real exchange rate API in production
  // For now, return the same amount as a placeholder
  console.warn("Currency conversion not implemented yet. Using 1:1 ratio.");
  return amount;
};

/**
 * Validate currency code
 */
export const isValidCurrency = (currencyCode) => {
  return currencyCode && CURRENCY_CONFIG.hasOwnProperty(currencyCode);
};

export default {
  formatCurrency,
  getCurrentCurrency,
  getCurrencyConfig,
  getAntdFormatter,
  getAntdParser,
  getCurrencySymbol,
  getCurrencyAffix,
  formatCurrencyForTable,
  getCurrencyOptions,
  convertCurrency,
  isValidCurrency,
  CURRENCY_CONFIG,
};
